import { createRoomRentableUnit, deleteRoomRentableUnit, getParkingRentableUnit, getRentableUnits } from '@/api/rentable-units.api';
import { withLoading } from '@/shared';
import type { RentableUnit, RentableUnitExpandOpts } from '@condo/domain';
import type { GetRentableUnitQueryResponse } from '@condo/sdk/propx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRef, computed, isRef, unref } from 'vue';

export const useGetRentableUnits = (estateIdOrRef?: MaybeRef<number>, onlyActive?: boolean) => {
    const estateId = computed(() => unref(estateIdOrRef));
    const enabled = computed(() => !!unref(estateIdOrRef));

    return useQuery(
        ['estate', 'rentable-units', estateId, onlyActive],
        async (): Promise<RentableUnit[]> => {
            if (!estateId.value) {
                return [];
            }
            return withLoading(async () => getRentableUnits(estateId.value!, onlyActive));
        },
        {
            initialData: [],
            enabled,
        },
    );
};

export const useGetParkingRentableUnit = ({
    parkingSpaceId: parkingSpaceIdOrRef,
    expandOpts,
}: {
    parkingSpaceId?: MaybeRef<number>;
    expandOpts?: RentableUnitExpandOpts;
}) => {
    const parkingSpaceId = computed(() => unref(parkingSpaceIdOrRef));

    return useQuery(
        ['estate', 'parking-rentable-unit', parkingSpaceIdOrRef],
        async (): Promise<GetRentableUnitQueryResponse | null> => {
            if (!parkingSpaceId.value) {
                return null;
            }
            return withLoading(async () => getParkingRentableUnit(unref(parkingSpaceIdOrRef)!, expandOpts));
        },
        {
            initialData: null,
        },
    );
};

export const useCreateRentableUnit = () => {
    return useMutation(
        async ({ estateElementId, estateId }: { estateElementId: number; estateId: number }) => await createRoomRentableUnit(estateElementId, estateId),
    );
};

export const useDeleteRentableUnit = () => {
    const queryClient = useQueryClient();
    return useMutation(async (estateElementId: MaybeRef<number>) => {
        const elementId = isRef(estateElementId) ? unref(estateElementId) : estateElementId;
        await deleteRoomRentableUnit(elementId);
        queryClient.invalidateQueries({ queryKey: ['rentable-units'] });
    });
};
